// Adding or editing a counterparty from the register (TX-US-0016). A page, as the deal and
// tracker forms are: somewhere a link can point at, with the read view and the edit view apart.
//
// An edit carries the version it loaded; a save against a record someone changed since is
// refused and says so, rather than overwriting them. A rename meets the same two 409s as a
// creation - the index's, which is certain, and the gate's, which the BD answers here.
//
// Both modes ask for the engagement (TX-US-0037). Adding also takes files: they wait in the
// browser until the save has made the counterparty they belong to, and are uploaded to it then.
// If one fails the counterparty is already on the register, so the form does not offer to save
// it again - it says which file failed and points at the profile, where files are attached.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { createCounterparty, getCounterparty, updateCounterparty } from '../api.js';
import useAttachmentUploads from '../deliveries/attachments/useAttachmentUploads.js';
import { notify } from '../ui/notify.jsx';
import CounterpartyFields, { bodyFrom, draftFrom } from './CounterpartyFields.jsx';
import FilesNotAttached from './documents/FilesNotAttached.jsx';
import StagedFiles from './documents/StagedFiles.jsx';
import { DOCUMENTS_API } from './documents/uploadApi.js';
import DuplicateReview from './DuplicateReview.jsx';
import { unanswered } from './engagement.js';
import EngagementFields from './EngagementFields.jsx';
import useProducts from './useProducts.js';

export default function CounterpartyForm({ mode }) {
  const { counterpartyId } = useParams();
  const navigate = useNavigate();
  const { products } = useProducts();

  const [draft, setDraft] = useState(mode === 'create' ? draftFrom(null) : null);
  const [version, setVersion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [flagged, setFlagged] = useState(null);
  // The counterparty this form made, once it has and a file then failed to follow it.
  const [created, setCreated] = useState(null);
  const uploads = useAttachmentUploads(DOCUMENTS_API);

  useEffect(() => {
    if (mode !== 'edit') return undefined;
    let cancelled = false;
    getCounterparty(counterpartyId)
      .then((record) => {
        if (cancelled) return;
        setDraft(draftFrom(record));
        setVersion(record.version);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, counterpartyId]);

  const save = async (confirmedDistinct = false) => {
    setBusy(true);
    setError(null);
    try {
      const body = { ...bodyFrom(draft), ...(confirmedDistinct ? { confirmed_distinct: true } : {}) };
      const saved = mode === 'create'
        ? await createCounterparty(body)
        : await updateCounterparty(counterpartyId, { ...body, version });
      const { failed } = await uploads.drain(saved.counterparty_id);
      if (failed) {
        setCreated(saved);
        return;
      }
      notify(`${saved.name} saved.`);
      navigate(`/counterparties/${saved.counterparty_id}`);
    } catch (err) {
      if (err.status === 409 && err.detail?.kind === 'likely_duplicate') {
        setFlagged(err.detail);
      } else if (err.status === 409 && err.detail?.existing) {
        setError(`Already on the register as "${err.detail.existing.name}".`);
      } else if (err.status === 409) {
        setError('Someone else changed this counterparty since you opened it. Reload to see their version.');
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    save();
  };

  if (!draft) return <p className={error ? 'error' : 'muted'}>{error ?? 'Loading…'}</p>;
  if (created) return <FilesNotAttached counterparty={created} items={uploads.items} />;

  const back = mode === 'create' ? '/counterparties' : `/counterparties/${counterpartyId}`;
  const missing = unanswered(draft.engagement);
  const complete = Boolean(draft.name.trim()) && missing.length === 0;

  return (
    <form className="deal-form" onSubmit={onSubmit}>
      <header className="register-header">
        <h2>{mode === 'create' ? 'New counterparty' : `Edit ${draft.name}`}</h2>
      </header>

      {error && <p className="error">{error}</p>}

      <CounterpartyFields
        draft={draft}
        onChange={(updater) => {
          setDraft(updater);
          setFlagged(null);
        }}
        products={products}
      />

      <EngagementFields
        draft={draft.engagement}
        onChange={(engagement) => setDraft((prev) => ({ ...prev, engagement }))}
      />

      {mode === 'create' && <StagedFiles uploads={uploads} disabled={busy} />}

      {mode === 'create' && (
        <p className="hint">
          Added as a prospect. Approving a counterparty is a separate, credit decision.
        </p>
      )}

      {flagged && (
        <DuplicateReview
          flagged={flagged}
          onPick={(candidate) => navigate(`/counterparties/${candidate.counterparty_id}`)}
        />
      )}

      <div className="form-actions">
        {flagged ? (
          <button type="button" onClick={() => save(true)} disabled={busy || !complete}>
            {busy ? 'Saving…' : 'Save anyway'}
          </button>
        ) : (
          <button type="submit" disabled={busy || !complete}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        )}
        <Link to={back}>Cancel</Link>
        {draft.name.trim() && missing.length > 0 && (
          <p className="hint">Still to answer: {missing.join('; ')}.</p>
        )}
      </div>
    </form>
  );
}
